import MenuItem from '@mui/material/MenuItem';
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExportContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  useGridApiContext,
} from '@mui/x-data-grid';
import React from 'react';
import { languageLabel } from '../../../helpers/chatDashboardHelper';
import { formatUnixDate } from '../../../helpers/dashboardHelper';
import { useTranslation } from '../../../hooks/useTranslation';
import { AgentType } from '../../../interfaces/Agent';
import { Conversation } from '../../../interfaces/Analytics';
import { TranslationKey } from '../../../types/i18n';

export interface ConversationsTableToolbarProps {
  conversations?: Conversation[];
  agentType?: AgentType;
  // Off when the rows on screen are only a page of the result
  showExport?: boolean;
  // Set when the search box goes to the backend
  quickFilterDebounceMs?: number;
  quickFilterMaxLength?: number;
  quickFilterPlaceholder?: string;
}

const exportWithDetails = (
  conversations: Conversation[],
  agentType: AgentType,
  t: (key: TranslationKey) => string
) => {
  const dataCollectedKeys = Array.from(
    new Set(conversations.flatMap(c => Object.keys(c.data_collected ?? {})))
  );

  const baseHeaders =
    agentType === AgentType.CALL
      ? [
          t('Date & Time'),
          t('Agent'),
          t('Contact'),
          t('Duration'),
          t('Status'),
          t('Summary'),
          t('Cost'),
          t('Conversation ID'),
        ]
      : [
          t('Date & Time'),
          t('Agent'),
          t('Messages Count'),
          t('Duration'),
          t('Language'),
          t('Summary'),
          t('Conversation ID'),
        ];

  const headers = [
    ...baseHeaders,
    ...dataCollectedKeys.map(k => `${t('Data')}: ${k}`),
    t('Message Role'),
    t('Message Content'),
  ];

  const rows = conversations.flatMap(c => {
    const base =
      agentType === AgentType.CALL
        ? [
            formatUnixDate({ timestamp: c.start_ts }),
            c.agent_name,
            c.caller_phone_number ?? '',
            c.conversation_duration,
            c.status,
            c.conversation_summary ?? '',
            c.call_cost,
            c.conversation_id,
          ]
        : [
            formatUnixDate({ timestamp: c.start_ts }),
            c.agent_name,
            c.messages?.length ?? 0,
            c.conversation_duration,
            languageLabel(c.conversation_language, t),
            c.conversation_summary ?? '',
            c.conversation_id,
          ];

    const dcValues = dataCollectedKeys.map(k => c.data_collected?.[k] ?? '');

    const messages = c.messages ?? [];
    if (messages.length === 0) {
      return [[...base, ...dcValues, '', '']];
    }

    return messages.map(m => [...base, ...dcValues, m.role, m.content]);
  });

  const escape = (value: unknown) =>
    `"${String(value ?? '').replace(/"/g, '""')}"`;

  const csv = [headers, ...rows]
    .map(row => row.map(escape).join(','))
    .join('\n');

  const prefix = agentType === AgentType.CALL ? 'call' : 'chat';
  const idSuffix = conversations[0]?.conversation_id?.slice(0, 8) ?? '';
  const filename = idSuffix
    ? `${prefix}_conversation_details_${idSuffix}`
    : `${prefix}_conversation_details`;

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const CsvExportMenuItem: React.FC<{
  conversations: Conversation[];
  agentType: AgentType;
}> = ({ conversations, agentType }) => {
  const { t } = useTranslation();
  const apiRef = useGridApiContext();
  const prefix = agentType === AgentType.CALL ? 'call' : 'chat';
  const idSuffix = conversations[0]?.conversation_id?.slice(0, 8) ?? '';
  const fileName = idSuffix
    ? `${prefix}_conversations_${idSuffix}`
    : `${prefix}_conversations`;
  return (
    <MenuItem onClick={() => apiRef.current.exportDataAsCsv({ fileName })}>
      {t('Export Conversations')}
    </MenuItem>
  );
};

const ConversationsTableToolbar: React.FC<ConversationsTableToolbarProps> = ({
  conversations = [],
  agentType = AgentType.CALL,
  showExport = true,
  quickFilterDebounceMs,
  quickFilterMaxLength,
  quickFilterPlaceholder,
}) => {
  const { t } = useTranslation();

  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      {showExport && (
        <GridToolbarExportContainer>
          <CsvExportMenuItem
            conversations={conversations}
            agentType={agentType}
          />
          <MenuItem
            onClick={() => exportWithDetails(conversations, agentType, t)}
          >
            {t('Export Conversations with Details')}
          </MenuItem>
        </GridToolbarExportContainer>
      )}
      <GridToolbarQuickFilter
        debounceMs={quickFilterDebounceMs}
        placeholder={quickFilterPlaceholder}
        inputProps={{ maxLength: quickFilterMaxLength }}
      />
    </GridToolbarContainer>
  );
};

export default ConversationsTableToolbar;
